import { WSTableCellProps } from "@wingspanhq/fe-component-library";
import {
  InvoiceRow,
  PayeeEngagementType,
  PayStatement,
  WorkLog,
  WorkLogStatus
} from "@wingspanhq/wingspan-api-sdk";
import { selectorWorkLogConvertedResourceLabels } from "../selectors/selectorWorkLogConvertedResourceLabels";
import { WORK_LOG_STATUS_DISCARDED } from "../types";

type GetWorkLogStatusCellPropsParams = {
  workLog: WorkLog;
  /**
   * The invoice the work log converted into — an `Invoice` or its search-row
   * projection; only the number is read, so either shape fits.
   */
  invoice?: Pick<InvoiceRow, "invoiceNumber">;
  /**
   * The pay statement a W-2 work log converted into; carries the human-readable
   * number the design shows under Status. Passed only where it is already
   * loaded — never fetched per table row.
   */
  payStatement?: Pick<PayStatement, "payStatementNumber">;
  /** Names the resource an unapproved work log will become. */
  engagementType?: PayeeEngagementType | null;
};

export function getWorkLogPayeeStatusCellProps({
  workLog,
  invoice,
  payStatement,
  engagementType
}: GetWorkLogStatusCellPropsParams): WSTableCellProps {
  const convertedResourceNoun = selectorWorkLogConvertedResourceLabels({
    convertedResourceType: workLog.convertedResourceType,
    engagementType
  }).noun;

  // "Discarded" is not in the SDK WorkLogStatus enum yet. Neutral rather than an
  // error theme: an empty auto-submitted work log being discarded is the normal
  // outcome, not a failure.
  if ((workLog.status as string) === WORK_LOG_STATUS_DISCARDED) {
    return {
      secondaryText: `No ${convertedResourceNoun} created`,
      pill: {
        text: "Discarded",
        theme: "neutral",
        icon: "info-circle"
      }
    };
  }

  switch (workLog.status) {
    case WorkLogStatus.APPROVED:
      return {
        secondaryText: payStatement?.payStatementNumber
          ? `Pay statement #${payStatement.payStatementNumber}`
          : invoice?.invoiceNumber
            ? `Invoiced #${invoice.invoiceNumber}`
            : undefined,
        pill: {
          text: "Approved",
          theme: "success",
          icon: "check-circle"
        }
      };

    case WorkLogStatus.IN_PROGRESS:
      return {
        secondaryText: "Returned for editing",
        secondaryTextColor: "amber400",
        pill: {
          text: "Action required",
          theme: "warning",
          icon: "alert-circle"
        }
      };

    case WorkLogStatus.IN_REVIEW:
      return {
        secondaryText: "Awaiting client approval",
        pill: {
          text: "Submitted",
          theme: "blue",
          icon: "time"
        }
      };

    case WorkLogStatus.REJECTED:
      return {
        secondaryText: workLog.convertedResourceId
          ? `Cancelled ${convertedResourceNoun}`
          : `No ${convertedResourceNoun} created`,
        pill: {
          text: "Rejected",
          theme: "error",
          icon: "alert-circle"
        }
      };

    case WorkLogStatus.DRAFT:
    default:
      return {
        secondaryText: "Awaiting your submission",
        pill: {
          text: "Draft",
          theme: "neutral",
          icon: "draft"
        }
      };
  }
}
